// SoCal hand-drawn service-area map: coastline, ocean waves, city pins, palms, cactus, sun
const PINS = [
  { x: 180, y: 200, name: "Ventura", note: "~80mi" },
  { x: 320, y: 290, name: "Malibu", note: "~55mi" },
  { x: 410, y: 340, name: "DTLA", note: "~28mi" },
  { x: 510, y: 380, name: "Pasadena", note: "~35mi" },
  { x: 460, y: 470, name: "Long Beach", note: "HQ", hq: true },
  { x: 600, y: 540, name: "Newport", note: "~22mi" },
  { x: 760, y: 510, name: "Riverside (IE)", note: "~50mi" },
  { x: 950, y: 360, name: "Joshua Tree", note: "~130mi" },
  { x: 870, y: 720, name: "San Diego", note: "~105mi" },
];

export default function CoastMap() {
  return (
    <section id="service-area" className="relative bg-[#e8dcc4] overflow-hidden border-t border-b border-[#c9bfa6] scroll-mt-32">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* LEFT — copy */}
          <div className="lg:col-span-4">
            <span className="text-xs font-semibold uppercase tracking-[0.35em] text-[#c97b3a]">005 — Service Area</span>
            <h2 className="font-display text-5xl md:text-6xl text-[#1f2e1f] leading-[0.9] mt-3">
              From the coast<br />
              <span className="italic text-[#c97b3a]">to the canyons.</span>
            </h2>
            <p className="text-[#5a5245] mt-5 leading-relaxed">
              We're hitched up in Long Beach and ready to roll across SoCal — Belmont Shore weddings, Malibu cliff-tops, Newport backyards, Joshua Tree desert vows, San Diego rooftops. If your venue's between the Pacific and the Mojave, we've got you.
            </p>
            <ul className="mt-7 space-y-2 text-sm">
              <li className="flex items-center gap-3"><span className="h-2 w-2 rounded-full bg-[#c97b3a]" /> <span className="text-[#1f2e1f] font-medium">Free travel within 25 miles of Long Beach</span></li>
              <li className="flex items-center gap-3"><span className="h-2 w-2 rounded-full bg-[#1f2e1f]" /> <span className="text-[#5a5245]">$2/mile beyond — quoted upfront, no surprises</span></li>
            </ul>
          </div>

          {/* RIGHT — the map */}
          <div className="lg:col-span-8 relative">
            <div className="absolute -top-4 -right-4 bg-[#1f2e1f] text-[#e8dcc4] px-3 py-1 rotate-3 text-[10px] uppercase tracking-[0.3em] font-bold z-10">Pacific · LA · OC · IE · SD</div>
            <svg viewBox="0 0 1200 820" className="w-full h-auto" aria-label="Trailhead Taps SoCal service area map" data-testid="coast-map">
              {/* OCEAN background */}
              <rect width="1200" height="820" fill="#cfddd5" />

              {/* Land mass — irregular SoCal shape */}
              <path
                d="M 0 0 L 1200 0 L 1200 820 L 980 820 Q 920 760 870 740 Q 800 720 760 680 Q 720 640 700 600 Q 680 560 640 540 Q 600 520 560 560 Q 540 580 500 560 Q 460 540 440 500 Q 420 460 460 440 Q 500 420 520 380 Q 540 340 500 320 Q 460 300 440 260 Q 420 220 380 200 Q 340 180 300 200 Q 260 220 220 200 Q 180 180 140 160 Q 100 140 60 100 Q 30 60 0 40 Z"
                fill="#dccfb3"
                stroke="#a3937a"
                strokeWidth="2"
                strokeLinejoin="round"
              />

              {/* Ocean wave squiggles */}
              {[
                [40, 480, 220], [80, 560, 180], [60, 660, 240], [180, 720, 200],
                [280, 380, 140], [120, 380, 160],
              ].map(([x, y, w], i) => (
                <path key={i} d={`M ${x} ${y} q ${w/8} -8 ${w/4} 0 t ${w/4} 0 t ${w/4} 0 t ${w/4} 0`}
                  fill="none" stroke="#7e9aa1" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
              ))}

              {/* Sun in top-right corner */}
              <g transform="translate(1020, 80) scale(0.55)" style={{ color: "#f5b85c" }}>
                <use href="#sun" />
              </g>

              {/* Decorative palms scattered on land */}
              {[
                [560, 410, 0.55], [680, 380, 0.5], [560, 560, 0.5],
              ].map(([x, y, s], i) => (
                <g key={`p${i}`} transform={`translate(${x}, ${y}) scale(${s})`} style={{ color: "#1f2e1f" }}>
                  <use href="#palm" />
                </g>
              ))}

              {/* Cacti near Joshua Tree (right side) */}
              {[
                [880, 340, 0.7], [990, 380, 0.6], [930, 410, 0.55],
              ].map(([x, y, s], i) => (
                <g key={`c${i}`} transform={`translate(${x}, ${y}) scale(${s})`} style={{ color: "#3d5a3d" }}>
                  <use href="#cactus" />
                </g>
              ))}

              {/* Pines toward the canyons */}
              {[
                [800, 460, 0.6], [840, 500, 0.5], [720, 480, 0.55],
              ].map(([x, y, s], i) => (
                <g key={`pn${i}`} transform={`translate(${x}, ${y}) scale(${s})`} style={{ color: "#1f2e1f" }}>
                  <use href="#pine" />
                </g>
              ))}

              {/* 25-mile free travel ring around Long Beach HQ */}
              <circle cx="460" cy="470" r="120" fill="none" stroke="#c97b3a" strokeWidth="2" strokeDasharray="6 8" opacity="0.7" />
              <text x="460" y="350" textAnchor="middle" fontSize="14" fill="#c97b3a" fontFamily="DM Sans, sans-serif" fontWeight="600" letterSpacing="2">25 MI FREE</text>

              {/* CITY PINS */}
              {PINS.map((p, i) => (
                <g key={i} transform={`translate(${p.x}, ${p.y})`}>
                  {/* pin shape */}
                  {p.hq ? (
                    <>
                      <circle cx="0" cy="0" r="16" fill="#c97b3a" />
                      <circle cx="0" cy="0" r="22" fill="none" stroke="#c97b3a" strokeWidth="2" />
                      <path d="M 0 0 L 0 28 L -10 16 Z" fill="#c97b3a" />
                    </>
                  ) : (
                    <>
                      <circle cx="0" cy="0" r="7" fill="#1f2e1f" />
                      <circle cx="0" cy="0" r="3" fill="#e8dcc4" />
                    </>
                  )}
                  <text x={p.hq ? 30 : 14} y="-2" fontSize={p.hq ? 22 : 16} fill="#1f2e1f" fontFamily="Fraunces, serif" fontWeight={p.hq ? 700 : 600}>
                    {p.name}
                  </text>
                  <text x={p.hq ? 30 : 14} y={p.hq ? 18 : 14} fontSize="11" fill="#5a5245" fontFamily="DM Sans, sans-serif" letterSpacing="1.5" textTransform="uppercase">
                    {p.note}
                  </text>
                </g>
              ))}

              {/* Pacific Ocean label */}
              <text x="120" y="200" fontSize="32" fill="#7e9aa1" fontFamily="Fraunces, serif" fontStyle="italic" opacity="0.7">Pacific</text>
              <text x="140" y="240" fontSize="32" fill="#7e9aa1" fontFamily="Fraunces, serif" fontStyle="italic" opacity="0.7">Ocean</text>

              {/* Mojave label */}
              <text x="900" y="500" fontSize="20" fill="#8c8577" fontFamily="Fraunces, serif" fontStyle="italic" opacity="0.6">Mojave</text>

              {/* Compass rose */}
              <g transform="translate(1130, 760)">
                <circle cx="0" cy="0" r="22" fill="#e8dcc4" stroke="#1f2e1f" strokeWidth="1.5" />
                <path d="M 0 -18 L 4 0 L 0 18 L -4 0 Z" fill="#c97b3a" />
                <path d="M -18 0 L 0 -4 L 18 0 L 0 4 Z" fill="#1f2e1f" />
                <text x="0" y="-26" textAnchor="middle" fontSize="10" fill="#1f2e1f" fontFamily="DM Sans" fontWeight="700">N</text>
              </g>
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
}
